import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";
import ButtonV2 from "../button/v2";
import SelectDropdownV2 from "../selectdropdown/v2";

export interface PaginationProps {
  pageNumber: number;
  pageSize: number;
  currentPageItemCount: number;
  isLoading?: boolean;
  onPageChange: (pageNumber: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
}

const PAGE_SIZE_OPTIONS = [
  { name: "10 per page", value: 10 },
  { name: "25 per page", value: 25 },
  { name: "50 per page", value: 50 },
  { name: "100 per page", value: 100 },
];

const Pagination: React.FC<PaginationProps> = ({
  pageNumber,
  pageSize,
  currentPageItemCount,
  isLoading = false,
  onPageChange,
  onPageSizeChange,
  className,
}) => {
  const hasNextPage = currentPageItemCount === pageSize;

  const selectedPageSize = PAGE_SIZE_OPTIONS.find(
    (option) => option.value === pageSize
  );

  const getPagesToRender = () => {
    const pages: number[] = [1];

    if (pageNumber <= 3) {
      if (pageNumber >= 2) pages.push(2);
    }

    const nearCurrent: number[] = [];
    if (pageNumber - 1 > 1) nearCurrent.push(pageNumber - 1);
    if (pageNumber !== 1) nearCurrent.push(pageNumber);
    if (hasNextPage) nearCurrent.push(pageNumber + 1);

    for (const p of nearCurrent) {
      if (!pages.includes(p)) pages.push(p);
    }

    if (pageNumber === 1 && hasNextPage && !pages.includes(2)) pages.push(2);
    if (pageNumber === 2 && hasNextPage && !pages.includes(3)) pages.push(3);

    return pages.filter((p) => p >= 1).sort((a, b) => a - b);
  };

  const pagesToRender = getPagesToRender();
  const showLeadingEllipsis = pageNumber > 3;
  const showTrailingEllipsis = hasNextPage;

  return (
    <div
      className={`relative mt-5 flex items-center justify-center gap-2 ${className ?? ""}`}
    >
      <ButtonV2
        variant="ghost"
        size="md"
        prefix={<ChevronLeft className="h-4 w-4 text-[#0A0A0A]" />}
        className="h-9 px-4 text-[#0A0A0A] border border-transparent hover:border-gray-200 hover:bg-transparent hover:text-[#0A0A0A]"
        disabled={isLoading || pageNumber === 1}
        onClick={() => onPageChange(pageNumber - 1)}
      >
        Previous
      </ButtonV2>

      <div className="flex items-center gap-1">
        {pagesToRender.map((p, idx) => {
          const isSelected = p === pageNumber;
          const renderLeadingEllipsisHere =
            showLeadingEllipsis && idx === 1 && p !== 2;

          return (
            <div key={p} className="flex items-center gap-1">
              {renderLeadingEllipsisHere && (
                <span className="px-1 text-sm font-medium text-[#0A0A0A]">
                  ...
                </span>
              )}
              <ButtonV2
                variant={isSelected ? "outline" : "ghost"}
                size="md"
                className={`h-9 w-9 px-0 text-[#0A0A0A] disabled:opacity-100 hover:text-[#0A0A0A] ${
                  isSelected
                    ? "bg-white border-gray-200 hover:border-gray-300 hover:bg-white"
                    : "border border-transparent hover:border-gray-200 hover:bg-transparent"
                }`}
                disabled={isLoading}
                onClick={() => {
                  if (p !== pageNumber) onPageChange(p);
                }}
              >
                {p}
              </ButtonV2>
            </div>
          );
        })}

        {showTrailingEllipsis && (
          <span className="px-1 text-sm font-medium text-[#0A0A0A]">...</span>
        )}
      </div>

      <ButtonV2
        variant="ghost"
        size="md"
        suffix={<ChevronRight className="h-4 w-4 text-[#0A0A0A]" />}
        className="h-9 px-4 text-[#0A0A0A] border border-transparent hover:border-gray-200 hover:bg-transparent hover:text-[#0A0A0A]"
        disabled={isLoading || !hasNextPage}
        onClick={() => onPageChange(pageNumber + 1)}
      >
        Next
      </ButtonV2>

      {onPageSizeChange && selectedPageSize && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2">
          <SelectDropdownV2
            name="Page Size"
            options={PAGE_SIZE_OPTIONS}
            selectedOption={selectedPageSize}
            onSelectionChange={(option) =>
              onPageSizeChange(option.value)
            }
            enableSearch={false}
            labelClassName="h-9 min-w-[130px] rounded-md border border-gray-200 bg-white px-3 text-sm text-[#0A0A0A]"
            selectedContainerClassName="border-gray-200"
          />
        </div>
      )}
    </div>
  );
};

export default Pagination;

